// src/pages/EditTitlePage.jsx
// Phase P3.1: Edit Title Page with TTS Processing (mirrors Independent Author flow)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import PublisherNav from '../components/PublisherNav';
import TTSDecisionModal from '../components/TTSDecisionModal';
import TTSProofReportModal from '../components/TTSProofReportModal';
// P0 DIRECTIVE: TTSProgressModal is DEPRECATED and FORBIDDEN
// TTS is background-only, progress shown via polling surfaces
import OutlineViewerModalV3 from '../components/OutlineViewerModalV3';
import PostIngestionOutlineEditor from '../components/PostIngestionOutlineEditor';
import PreFlightDiagnosticsPanel from '../components/PreFlightDiagnosticsPanel';
import StreamlinedTTSResumeModal from '../components/StreamlinedTTSResumeModal'; // PHASE 6: Resume modal
import SampleSelectionModal from '../components/SampleSelectionModal'; // Book Sample feature
import ActivationChargeConfirmModal from '../components/ActivationChargeConfirmModal';
import PaymentMethodRequiredModal from '../components/PaymentMethodRequiredModal';
import { BOOK_TYPE_OPTIONS, GENRE_OPTIONS, getGenresForBookType, getThemesForGenre } from '../constants/genreThemes';
import { 
  BookOpen, ArrowLeft, Save, Upload, Image, FileText, CheckCircle, Clock, 
  AlertCircle, Archive, AlertTriangle, CheckSquare, Square, Play, Eye, 
  BarChart3, Loader2, Volume2, Edit, RefreshCw, RefreshCcw, Calendar, Users, MessageSquare, Brain, Wrench, Copy, Check, Music, Link as LinkIcon, ExternalLink
} from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useNotification } from '@/contexts/NotificationContext';
import { useAuth } from '@/contexts/AuthContext';
import { BACKEND_URL as backendUrl } from '@/config/api';
import { xhrFetchAuth, xhrFetchAuthPost } from '@/lib/xhrFetch';

const ATTESTATION_TEXT = `Publisher Upload Authorization & Rights Attestation

By proceeding, I confirm that I am acting on behalf of the Publisher identified in this account and that I am authorized to upload, distribute, and manage this work for the associated Author listed on this platform.

I attest that:

1. The Publisher has obtained all necessary rights, licenses, permissions, or contractual authority from the Author or rights holder to upload, reproduce, distribute, and make this work available through the NewAudia platform.

2. To the best of my knowledge, this work does not infringe upon any third-party copyrights, trademarks, or intellectual property rights.

3. I understand that NewAudia relies on this attestation and does not independently verify copyright ownership or authorization.

4. I acknowledge that the Publisher is solely responsible for any disputes, claims, takedown requests, or legal actions arising from the upload, distribution, or use of this work.

5. I understand that NewAudia may remove or disable access to this work if a rights dispute, legal claim, or policy violation is reported.

I confirm that this attestation is truthful and made in good faith.`;

const EditTitlePage = () => {
  const { titleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const coverInputRef = useRef(null);
  const manuscriptInputRef = useRef(null);
  const pollingRef = useRef(null);
  const ttsCompletedRef = useRef(false); // Prevent polling loop after completion
  
  const [title, setTitle] = useState(null);
  const [manuscript, setManuscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [showCoverModal, setShowCoverModal] = useState(false);
  const [coverOptStatus, setCoverOptStatus] = useState(null); // null | 'uploading' | 'optimizing' | 'done' | 'error'
  const [uploadingManuscript, setUploadingManuscript] = useState(false);
  
  // Manuscript upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [manuscriptFile, setManuscriptFile] = useState(null);
  const [uploadAttestation, setUploadAttestation] = useState(false);
  
  // TTS modals - P0: Progress modal state removed, only decision modal needed
  const [showTTSModal, setShowTTSModal] = useState(false);
  
  // PHASE 6: Resume modal for partial audio completion
  const [showResumeModal, setShowResumeModal] = useState(false);
  
  // TTS Regeneration Modal - for showing progress when segments need regeneration
  const [showTTSRegenModal, setShowTTSRegenModal] = useState(false);
  const [ttsRegenStatus, setTTSRegenStatus] = useState({
    isProcessing: false,
    segmentsToProcess: [],
    segmentsCompleted: 0,
    currentSegment: null,
    error: null
  });
  
  // Reconciliation state - for completing stuck ingestions
  const [isReconciling, setIsReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);
  
  // Activation state
  const [isActivating, setIsActivating] = useState(false);
  const [indexingInFlight, setIndexingInFlight] = useState(false);
  const [indexError, setIndexError] = useState(null);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [activationAction, setActivationAction] = useState(null); // 'activate' or 'deactivate'
  const [showActivationProgress, setShowActivationProgress] = useState(false);
  const [activationProgressStep, setActivationProgressStep] = useState('');
  const [activationProgressSteps, setActivationProgressSteps] = useState([]);
  
  // Outline modal
  const [showOutlineModal, setShowOutlineModal] = useState(false);
  
  // Post-Ingestion Outline Editor modal
  const [showPostEditModal, setShowPostEditModal] = useState(false);
  
  // P0 PROOF REPORT: One-click diagnostic package
  const [showProofReport, setShowProofReport] = useState(false);
  
  // Book Sample Selection modal
  const [showSampleModal, setShowSampleModal] = useState(false);
  
  // Payment gate state (Phase 3)
  const [showChargeConfirmModal, setShowChargeConfirmModal] = useState(false);
  const [chargeCardInfo, setChargeCardInfo] = useState({ last4: null, brand: null });
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  
  // Preview Share URL state - uses public sample route for social media sharing
  const [previewShareUrl, setPreviewShareUrl] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Generate the shareable URL when manuscript data is loaded
  useEffect(() => {
    if (title?.manuscript_id && manuscript?.hasSample) {
      setPreviewShareUrl(`${window.location.origin}/sample/${title.manuscript_id}`);
    }
  }, [title?.manuscript_id, manuscript?.hasSample]);
  
  // P0 TTS PROGRESS: Poll for progress when TTS is running
  const [ttsProgress, setTtsProgress] = useState(null);
  const ttsProgressIntervalRef = useRef(null);
  
  // Track updated character count from outline edits for accurate TTS cost estimation
  const [updatedCharacterCount, setUpdatedCharacterCount] = useState(null);
  
  // Book History state
  const [bookHistory, setBookHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const { notifyError, notifySuccess } = useNotification();
  
  // Post-Ingestion Outline Editor completion handler
  const handlePostEditComplete = (result) => {
    console.log('[EditTitlePage] Post-edit complete:', result);
    // Refresh data to show updated status
    fetchTitle();
    if (result?.deactivated) {
      notifySuccess('Manuscript updated! Please re-activate to make changes live.');
    }
  };
  
  // P0 Pre-Flight Diagnostics Panel state
  const [preflightResult, setPreflightResult] = useState(null);
  const [preflightBlocked, setPreflightBlocked] = useState(false);
  
  // Track original description to detect changes
  const [originalDescription, setOriginalDescription] = useState('');
  const [descriptionChanged, setDescriptionChanged] = useState(false);
  
  const [formData, setFormData] = useState({
    title_name: '',
    subtitle: '',
    description: '',
    isbn: '',
    book_type: '',
    genre: '',
    theme: '',
    purchase_url: '',
    is_promotional: false,
    status: 'draft'
  });
  
  const [errors, setErrors] = useState({});
  // backendUrl imported from centralized config as BACKEND_URL

  useEffect(() => {
    fetchTitle();
    fetchBookHistory();
    
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [titleId]);

  // Check if coming from upload with TTS modal request
  useEffect(() => {
    if (location.state?.fromUpload && location.state?.showTTSModal && manuscript) {
      // Show TTS decision modal after a brief delay
      setTimeout(() => {
        setShowTTSModal(true);
      }, 500);
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [manuscript, location.state]);

  // P0 CANONICAL POLLING: ONLY use /tts/progress endpoint for all TTS state
  // This is the SINGLE SOURCE OF TRUTH - no inference from manuscript fields
  useEffect(() => {
    if (!titleId || !manuscript) return;
    
    const pollTTSProgress = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${backendUrl}/api/publisher/titles/${titleId}/tts/progress`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('[EditTitlePage] TTS progress:', data);
          setTtsProgress(data);
          
          // Stop polling when terminal state reached AND activation ready
          const ttsState = data.tts?.state || 'NONE';
          const canActivate = data.activation?.can_activate || false;
          
          if (ttsState === 'DONE' && canActivate) {
            console.log('[EditTitlePage] TTS DONE + can_activate=true, stopping poll');
            if (ttsProgressIntervalRef.current) {
              clearInterval(ttsProgressIntervalRef.current);
              ttsProgressIntervalRef.current = null;
            }
            // Refresh title data to sync other UI elements
            fetchTitle();
          } else if (['FAIL', 'BLOCKED'].includes(ttsState)) {
            console.log('[EditTitlePage] TTS terminal state:', ttsState);
            if (ttsProgressIntervalRef.current) {
              clearInterval(ttsProgressIntervalRef.current);
              ttsProgressIntervalRef.current = null;
            }
          }
        }
      } catch (err) {
        console.error('[EditTitlePage] TTS progress poll error:', err);
      }
    };
    
    // Fetch immediately on mount/manuscript change
    pollTTSProgress();
    
    // Determine if we should continue polling based on ttsProgress state
    // Poll while: QUEUED, EXECUTING, FINALIZING, or DONE without can_activate
    const ttsState = ttsProgress?.tts?.state || 'NONE';
    const canActivate = ttsProgress?.activation?.can_activate || false;
    
    const shouldContinuePoll = ['QUEUED', 'EXECUTING', 'FINALIZING'].includes(ttsState) ||
                               (ttsState === 'DONE' && !canActivate);
    
    if (shouldContinuePoll && !ttsProgressIntervalRef.current) {
      // Start polling every 5 seconds
      ttsProgressIntervalRef.current = setInterval(pollTTSProgress, 5000);
    } else if (!shouldContinuePoll && ttsProgressIntervalRef.current) {
      // Stop polling
      clearInterval(ttsProgressIntervalRef.current);
      ttsProgressIntervalRef.current = null;
    }
    
    return () => {
      if (ttsProgressIntervalRef.current) {
        clearInterval(ttsProgressIntervalRef.current);
        ttsProgressIntervalRef.current = null;
      }
    };
  }, [titleId, manuscript?.id, ttsProgress?.tts?.state, ttsProgress?.activation?.can_activate, backendUrl]);

  // P0 FINAL HOLE: TTS polling REMOVED - startPolling DELETED
  // Status comes from page refresh only
  
  // TTS Regeneration polling - separate from general polling
  const ttsRegenPollingRef = useRef(null);
  
  const startTTSRegenPolling = (segmentsToProcess) => {
    if (ttsRegenPollingRef.current) clearInterval(ttsRegenPollingRef.current);
    
    ttsRegenPollingRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${backendUrl}/api/manuscripts/${manuscript?.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          const completed = data.tts_segments_completed || 0;
          const total = data.tts_segments_total || segmentsToProcess.length;
          
          setTTSRegenStatus(prev => ({
            ...prev,
            segmentsCompleted: completed,
            currentSegment: segmentsToProcess[Math.min(completed, segmentsToProcess.length - 1)]
          }));
          
          // Check if TTS failed - stop polling and show error
          if (data.audio_status === 'failed') {
            console.error('[EditTitlePage] TTS regeneration failed');
            clearInterval(ttsRegenPollingRef.current);
            ttsRegenPollingRef.current = null;
            
            setTTSRegenStatus(prev => ({
              ...prev,
              isProcessing: false,
              error: 'TTS generation failed. Please try again or contact support.'
            }));
            return;
          }
          
          // Check if TTS is complete
          if (data.audio_status === 'completed' || completed >= total) {
            console.log('[EditTitlePage] TTS regeneration complete');
            clearInterval(ttsRegenPollingRef.current);
            ttsRegenPollingRef.current = null;
            
            setTTSRegenStatus(prev => ({
              ...prev,
              isProcessing: false,
              segmentsCompleted: total
            }));
            
            // Refresh data after short delay
            setTimeout(() => {
              fetchTitle();
              setShowTTSRegenModal(false);
            }, 1500);
          }
        }
      } catch (error) {
        console.error('[EditTitlePage] Error polling TTS status:', error);
      }
    }, 2000);
  };
  
  // Cleanup TTS regen polling on unmount
  useEffect(() => {
    return () => {
      if (ttsRegenPollingRef.current) clearInterval(ttsRegenPollingRef.current);
    };
  }, []);

  const fetchTitle = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/publisher/titles/${titleId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('[EditTitlePage] Fetched title:', {
          id: data.id,
          audio_status: data.audio_status,
          tts_generation_status: data.tts_generation_status,
          tts_segments_completed: data.tts_segments_completed,
          tts_segments_total: data.tts_segments_total
        });
        setTitle(data);
        const desc = data.description || '';
        setFormData({
          title_name: data.title_name || '',
          subtitle: data.subtitle || '',
          description: desc,
          isbn: data.isbn || '',
          book_type: data.book_type || '',
          genre: data.genre || '',
          theme: data.theme || '',
          purchase_url: data.purchase_url || '',
          is_promotional: data.is_promotional || false,
          status: data.status || 'draft'
        });
        setOriginalDescription(desc);
        setDescriptionChanged(false);
        
        // Fetch manuscript if available (pass the title description to avoid overwriting)
        if (data.manuscript_id) {
          await fetchManuscript(data.manuscript_id, desc);
        }
      } else {
        notifyError('Title not found');
        navigate('/publisher/authors');
      }
    } catch (error) {
      console.error('Error fetching title:', error);
      notifyError('Failed to load title');
    } finally {
      setLoading(false);
    }
  };

  const fetchManuscript = async (manuscriptId, titleDescription = null) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/manuscripts/${manuscriptId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setManuscript(data);
        
        // Only populate description from manuscript summary if title has no description yet
        // Use the passed titleDescription to check, as formData state may not be updated yet
        if (data.summary && !titleDescription) {
          setFormData(prev => ({ ...prev, description: data.summary }));
          setOriginalDescription(data.summary);
        }
      }
    } catch (error) {
      console.error('Error fetching manuscript:', error);
    }
  };

  const fetchBookHistory = async () => {
    if (!titleId) return;
    
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/publisher/titles/${titleId}/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBookHistory(data);
      }
    } catch (error) {
      console.error('Error fetching book history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // P0 FINAL HOLE: TTS polling REMOVED - Status comes from initial page load only
  // fetchManuscriptStatus DELETED - No polling allowed
  // UI shows DONE/FAIL from initial fetch - no real-time updates

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ 
      ...prev, 
      [name]: newValue
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
    // Track description changes
    if (name === 'description') {
      setDescriptionChanged(newValue !== originalDescription);
    }
  };

  const handleSave = async () => {
    if (!formData.title_name.trim()) {
      notifyError('Title name is required');
      return;
    }
    
    setSaving(true);
    console.log('[EditTitlePage] Saving formData:', formData);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/publisher/titles/${titleId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      console.log('[EditTitlePage] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[EditTitlePage] Response data:', data);
        setTitle(data);
        // Also update formData with the returned data to reflect saved state
        setFormData(prev => ({
          ...prev,
          description: data.description || ''
        }));
        
        // If description changed and we have a manuscript, also update the manuscript summary
        if (descriptionChanged && title?.manuscript_id) {
          console.log('[EditTitlePage] Updating manuscript summary...');
          const summaryResponse = await fetch(`${backendUrl}/api/manuscripts/${title.manuscript_id}/summary`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 
              summary: formData.description,
              short_description: formData.description
            })
          });
          if (summaryResponse.ok) {
            console.log('[EditTitlePage] Manuscript summary updated successfully');
          } else {
            console.error('[EditTitlePage] Failed to update manuscript summary');
          }
        }
        
        setOriginalDescription(data.description || '');
        setDescriptionChanged(false);
        // Navigation is sufficient feedback - no toast needed
        
        // Navigate back to the Author Console page
        if (title?.publisher_author_id) {
          navigate(`/publisher/authors/${title.publisher_author_id}/console`);
        } else {
          navigate('/publisher/authors');
        }
      } else {
        // Handle error response — use .text() to avoid body-already-read errors from fetch interceptors
        try {
          const errorText = await response.text();
          let errorDetail = `Failed to update title (status ${response.status})`;
          try {
            const errorData = JSON.parse(errorText);
            errorDetail = errorData.detail || errorDetail;
          } catch (_) {}
          console.log('[EditTitlePage] Error response:', errorText);
          notifyError(errorDetail);
        } catch (parseError) {
          console.error('[EditTitlePage] Failed to read error response:', parseError);
          notifyError(`Failed to update title (status ${response.status})`);
        }
      }
    } catch (error) {
      console.error('Error updating title:', error);
      notifyError('Failed to update title');
    } finally {
      setSaving(false);
    }
  };

  // Cover upload
  const handleCoverSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      notifyError('Invalid file type. Please upload PNG, JPG, GIF, or WebP.');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      notifyError('File too large. Maximum 10MB.');
      return;
    }
    
    setCoverOptStatus('uploading');
    setShowCoverModal(false);
    setUploadingCover(true);
    
    try {
      const manuscriptId = manuscript?.id || title?.manuscript_id;
      const formData = new FormData();
      formData.append('cover', file);
      
      const res = await xhrFetchAuth(`${backendUrl}/api/covers/${manuscriptId}/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (res.ok && res.json?.cover_image_url) {
        setCoverOptStatus('done');
        toast.success('Cover uploaded and optimized');
        // Re-fetch title to get updated cover fields from database
        try {
          const titleRes = await fetch(`${backendUrl}/api/publisher/titles/${titleId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (titleRes.ok) {
            const freshTitle = await titleRes.json();
            setTitle(freshTitle);
          }
        } catch (fetchErr) {
          console.warn('Could not refresh title after cover upload:', fetchErr);
        }
      } else {
        setCoverOptStatus('error');
        notifyError(res.json?.detail || 'Failed to upload cover');
      }
    } catch (error) {
      console.error('Cover upload error:', error);
      setCoverOptStatus('error');
      notifyError(error.message || 'Failed to upload cover');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleOptimizeExistingCover = async () => {
    setCoverOptStatus('optimizing');
    setShowCoverModal(false);
    setUploadingCover(true);
    
    try {
      const manuscriptId = manuscript?.id || title?.manuscript_id;
      const res = await xhrFetchAuthPost(`${backendUrl}/api/covers/${manuscriptId}/optimize`, {});
      
      if (res.ok) {
        setCoverOptStatus('done');
        if (res.json?.optimized) {
          toast.success('Cover optimized successfully');
        } else {
          toast.info(res.json?.message || 'Cover already optimized');
        }
        // Re-fetch title to get updated cover fields from database
        try {
          const titleRes = await fetch(`${backendUrl}/api/publisher/titles/${titleId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (titleRes.ok) {
            const freshTitle = await titleRes.json();
            setTitle(freshTitle);
          }
        } catch (fetchErr) {
          console.warn('Could not refresh title after cover optimize:', fetchErr);
        }
      } else {
        setCoverOptStatus('error');
        notifyError(res.json?.detail || 'Failed to optimize cover');
      }
    } catch (error) {
      console.error('Cover optimize error:', error);
      setCoverOptStatus('error');
      notifyError(error.message || 'Failed to optimize cover');
    } finally {
      setUploadingCover(false);
    }
  };

  // P0 FINAL HOLE: User-initiated TTS generation is FORBIDDEN
  // TTS is auto-triggered by the system after ingestion completes
  // handleStartTTS DELETED - No user action allowed

  // Reconcile stuck manuscript - checks if audio exists and completes the ingestion
  const handleReconcile = async () => {
    if (!title?.id) return;
    
    setIsReconciling(true);
    setReconcileResult(null);
    
    try {
      const token = localStorage.getItem('accessToken');
      
      // Use the publisher-specific finalize endpoint
      const response = await fetch(`${backendUrl}/api/publisher/titles/${title.id}/finalize`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Finalization failed');
      }
      
      const result = await response.json();
      setReconcileResult(result);
      console.log('[EditTitlePage] Finalize result:', result);
      
      if (result.status === 'completed') {
        notifySuccess(result.message || 'Book finalized and ready for activation!');
        // Refresh title data to show updated status
        fetchTitle();
      } else if (result.status === 'partial') {
        notifyError(`${result.message} Try again in a moment.`);
      } else if (result.status === 'no_audio') {
        notifyError(result.message || 'No audio files found. Please generate audio first.');
      } else {
        notifyError(result.message || 'Could not finalize the book.');
      }
      
    } catch (error) {
      console.error('[EditTitlePage] Reconcile error:', error);
      notifyError(`Finalization failed: ${error.message}`);
    } finally {
      setIsReconciling(false);
    }
  };

  // REMOVED: Hard Reset function - P0 UX Directive: No user-operated recovery
  // Recovery is now backend-owned and automatic

  const handleSkipTTS = () => {
    setShowTTSModal(false);
    // Silent skip - user can generate audio later
    console.log('[EditTitlePage] User skipped TTS - can generate later');
  };

  const handlePreview = async () => {
    if (!title?.manuscript_id) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/manuscripts/${title.manuscript_id}/open`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        navigate(`/player/${data.session_id}`, { 
          state: { returnTo: `/publisher/titles/${titleId}/edit` } 
        });
      } else {
        notifyError('Failed to open preview');
      }
    } catch (error) {
      console.error('Error opening preview:', error);
      notifyError('Failed to open preview');
    }
  };

  // Preview the book sample - NO credits debited, NO analytics tracked
  const handlePreviewSample = async () => {
    if (!title?.manuscript_id) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/manuscripts/${title.manuscript_id}/sample/preview`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        navigate(`/player/${data.session_id}`, { 
          state: { 
            returnTo: `/publisher/titles/${titleId}/edit`,
            samplePreview: true  // Flag for player to load sample segments only
          } 
        });
      } else {
        const errorData = await response.json();
        notifyError(errorData.detail || 'Failed to preview sample');
      }
    } catch (error) {
      console.error('Error opening sample preview:', error);
      notifyError('Failed to preview sample');
    }
  };
  
  // Copy preview URL to clipboard
  const copyPreviewUrl = async () => {
    if (!previewShareUrl) return;
    
    try {
      await navigator.clipboard.writeText(previewShareUrl);
      setCopySuccess(true);
      notifySuccess('Link copied to clipboard!');
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('[COPY] Failed:', error);
      notifyError('Failed to copy link');
    }
  };

  const getCoverUrl = () => {
    if (title?.cover_image_url) {
      return `${backendUrl}${title.cover_image_url}?t=${Date.now()}`;
    }
    return null;
  };

  const getProcessingStatus = () => {
    // P0 FIX: Check both manuscript and title for audio status
    // The /api/publisher/titles/{id} endpoint includes audio fields from the manuscript
    // so we can use title as fallback when manuscript fetch fails (e.g., inactive books)
    const source = manuscript || title;
    console.log('[EditTitlePage] getProcessingStatus called:', {
      hasManuscript: !!manuscript,
      hasTitle: !!title,
      source_audio_status: source?.audio_status,
      source_tts_generation_status: source?.tts_generation_status
    });
    if (!source) return null;
    
    // DEFENSIVE: Use optional chaining for all property access
    // Older manuscripts may not have these Phase 5 fields
    const audioStatus = source?.audio_status || source?.tts_generation_status;
    const processingStatus = source?.processing_status;
    console.log('[EditTitlePage] audioStatus:', audioStatus, 'processingStatus:', processingStatus);
    
    // P0.7: Handle STALE status - outline was edited after TTS started
    if (audioStatus === 'stale' || audioStatus === 'STALE') {
      return { 
        status: 'stale', 
        label: 'Outline Changed - Save to Continue', 
        color: 'amber',
        message: 'Outline was edited. Save outline changes to define final audio scope, then restart TTS.'
      };
    }
    
    // PHASE 5: Handle partial audio status separately
    if (audioStatus === 'partial') {
      const completed = source?.audio_segments_completed || source?.tts_segments_completed || 0;
      const total = source?.audio_segments_total || source?.tts_segments_total || 0;
      return { 
        status: 'partial', 
        label: `Audio Incomplete (${completed}/${total})`, 
        color: 'amber',
        message: `${completed} of ${total} segments have audio. Complete remaining segments before activating.`,
        completedCount: completed,
        totalCount: total
      };
    }
    
    // Simple ingest uses 'ready' status when audio is complete
    if (audioStatus === 'completed' || audioStatus === 'ready' || processingStatus === 'completed') {
      return { status: 'completed', label: 'Audio Ready', color: 'emerald' };
    }
    if (audioStatus === 'processing' || processingStatus === 'processing' || processingStatus === 'queued') {
      return { status: 'processing', label: 'Generating Audio...', color: 'amber' };
    }
    if (audioStatus === 'failed' || processingStatus === 'failed') {
      return { status: 'failed', label: 'Audio Failed', color: 'red' };
    }
    if (source?.status === 'processed' || source?.manuscript_status === 'inactive') {
      // For simple ingest, inactive status with completed audio means ready
      if (audioStatus === 'completed') {
        return { status: 'completed', label: 'Audio Ready', color: 'emerald' };
      }
      return { status: 'ready', label: 'Ready for Audio', color: 'blue' };
    }
    return { status: 'pending', label: 'Pending', color: 'slate' };
  };
  
  // P0 Pre-Flight handler - updates blocking state for TTS gating
  const handlePreflightResult = useCallback(({ status, data }) => {
    setPreflightResult(data);
    setPreflightBlocked(status === 'blocked');
  }, []);
  
  // Determine if title is in processing state (for auto-refresh)
  const isProcessingState = () => {
    const audioStatus = manuscript?.audio_status;
    const procStatus = manuscript?.processing_status;
    return audioStatus === 'processing' || audioStatus === 'pending' || 
           procStatus === 'processing' || procStatus === 'queued' ||
           title?.status === 'processing';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50">
        <PublisherNav />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-t-blue-500 border-r-transparent border-b-indigo-500 border-l-transparent"></div>
        </div>
      </div>
    );
  }


  // SEO Phase 3: Index manuscript for public search crawlability
  const handleIndexManuscript = async () => {
    setIndexingInFlight(true);
    setIndexError(null);
    try {
      const res = await xhrFetchAuthPost(`${backendUrl}/api/manuscripts/${manuscript.id}/index`, {});
      if (!res.ok || !res.json?.seo_slug) {
        setIndexError(res.json?.detail || res.error?.message || 'Indexing failed — please try again');
        return;
      }
      // Update local state directly from response — no page reload needed
      setManuscript(prev => ({
        ...prev,
        seo_indexed: true,
        seo_slug: res.json.seo_slug,
      }));
      toast.success('Manuscript indexed successfully');
    } catch (err) {
      setIndexError('Indexing failed — please try again');
    } finally {
      setIndexingInFlight(false);
    }
  };

  // Phase 3: Payment gate for activation
  const handleActivationWithPaymentGate = async (originalActivationFn) => {
    const jobId = manuscript?.simple_ingest_job_id;
    if (!jobId) {
      // No ingest job — proceed directly (legacy activation path)
      originalActivationFn();
      return;
    }
    
    try {
      const checkRes = await xhrFetchAuth(`${backendUrl}/api/payments/activation-fee-check?job_id=${jobId}`);
      
      if (!checkRes.ok) {
        notifyError(checkRes.json?.detail || 'Failed to check activation fee');
        return;
      }
      
      const feeCheck = checkRes.json;
      
      if (!feeCheck.needs_payment) {
        originalActivationFn();
        return;
      }
      
      if (feeCheck.payment_method_required) {
        setShowActivationModal(false);
        setShowPaymentMethodModal(true);
        return;
      }
      
      // Show charge confirm modal
      setShowActivationModal(false);
      setChargeCardInfo({ last4: feeCheck.last4, brand: feeCheck.brand });
      setShowChargeConfirmModal(true);
      
    } catch (err) {
      console.error('Payment gate error:', err);
      notifyError('Failed to check activation fee');
    }
  };
  
  const handleChargeConfirmedAndActivate = async () => {
    setShowChargeConfirmModal(false);
    const jobId = manuscript?.simple_ingest_job_id;
    
    try {
      const chargeRes = await xhrFetchAuthPost(`${backendUrl}/api/payments/charge-activation-fee`, { job_id: jobId });
      
      if (!chargeRes.ok || !chargeRes.json?.success) {
        if (chargeRes.json?.reason === 'payment_failed') {
          notifyError(`Payment failed — ${chargeRes.json?.error || 'Unknown error'}. Your manuscript was not activated.`);
        } else if (chargeRes.json?.reason === 'payment_method_required') {
          setShowPaymentMethodModal(true);
        } else {
          notifyError(chargeRes.json?.detail || 'Charge failed');
        }
        return;
      }
      
      // Charge succeeded — re-open activation modal and proceed
      setActivationAction('activate');
      setShowActivationModal(true);
    } catch (err) {
      notifyError('Payment failed: ' + err.message);
    }
  };
  
  const handlePaymentMethodAddedForActivation = () => {
    setShowPaymentMethodModal(false);
    // Re-trigger activation flow
    setActivationAction('activate');
    setShowActivationModal(true);
  };

  const coverUrl = getCoverUrl();
  const processingStatus = getProcessingStatus();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <PublisherNav />
      
      <main className="mx-auto max-w-5xl px-4 py-8">
        {/* Back button */}
        <Link
          to={`/publisher/authors/${title?.publisher_author_id}/console`}
          className="flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {title?.author_pen_name || 'Author'}&apos;s Console
        </Link>

        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="rounded-full bg-indigo-600/20 p-2">
                <BookOpen className="w-6 h-6 text-indigo-400" />
              </div>
              <h1 className="text-2xl font-bold">{title?.title_name}</h1>
            </div>
            <p className="text-slate-400">
              By <strong className="text-slate-200">{title?.author_pen_name}</strong>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Cover & Status */}
          <div className="space-y-6">
            {/* Cover */}
            <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-5">
              <h2 className="text-lg font-semibold mb-4">Cover Image</h2>
              <input
                type="file"
                ref={coverInputRef}
                onChange={handleCoverSelect}
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
              />
              <div
                onClick={() => setShowCoverModal(true)}
                className="aspect-[3/4] rounded-xl border-2 border-dashed border-slate-600 bg-slate-800 flex items-center justify-center cursor-pointer hover:border-indigo-500 hover:bg-slate-700 transition-colors overflow-hidden relative"
              >
                {coverUrl ? (
                  <>
                    <img src={coverUrl} alt="Cover" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity">
                      <Upload className="w-8 h-8 text-white" />
                    </div>
                  </>
                ) : (
                  <div className="text-center p-4">
                    <Image className="w-10 h-10 text-slate-500 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">Click to upload cover</p>
                    <p className="text-xs text-slate-500 mt-1">PNG, JPG, GIF, WebP (max 10MB)</p>
                  </div>
                )}
                {uploadingCover && (
                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center flex-col gap-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
                    <p className="text-xs text-white">
                      {coverOptStatus === 'uploading' ? 'Optimizing image for NewAudia...' : 
                       coverOptStatus === 'optimizing' ? 'Optimizing existing cover...' : 'Processing...'}
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* Audio Status - P0: ONLY uses /tts/progress canonical endpoint */}
            {manuscript && (
              <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-5">
                <h2 className="text-lg font-semibold mb-4">
                  <span>Audio Status</span>
                </h2>
                
                {/* P0 UI MAPPING: Derive ALL state from ttsProgress canonical endpoint */}
                {(() => {
                  // Extract state from canonical endpoint response
                  const ttsState = ttsProgress?.tts?.state || 'NONE';
                  const completedSegments = ttsProgress?.tts?.completed_segments || 0;
                  const totalSegments = ttsProgress?.tts?.total_segments || 0;
                  const percent = ttsProgress?.tts?.percent || 0;
                  const failReason = ttsProgress?.tts?.fail_reason;
                  const blockingReason = ttsProgress?.tts?.blocking_reason;
                  const canActivate = ttsProgress?.activation?.can_activate || false;
                  const activationReason = ttsProgress?.activation?.reason || 'unknown';
                  
                  // STATE: DONE + can_activate = READY
                  if (ttsState === 'DONE' && canActivate) {
                    return (
                      <div className="space-y-4">
                        <div className="text-center p-4 bg-emerald-900/20 border border-emerald-500/30 rounded-xl">
                          <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                          <p className="text-lg text-emerald-300 font-semibold">Ready</p>
                          <p className="text-xs text-slate-400 mt-1">Your audiobook is ready for playback</p>
                        </div>
                        
                        <div className="grid grid-cols-1 gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePreview}
                            className="w-full text-xs"
                            data-testid="preview-btn"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Preview
                          </Button>
                        </div>
                        
                        {/* Create/Edit Sample Button - Book Sample feature for guest preview */}
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowSampleModal(true)}
                            className={`flex-1 text-xs ${
                              manuscript?.hasSample 
                                ? 'border-purple-500/50 text-purple-400 hover:bg-purple-500/10' 
                                : 'bg-sky-400 text-black border-sky-400 hover:bg-sky-300 hover:border-sky-300'
                            }`}
                            data-testid="create-sample-btn"
                          >
                            <Music className="w-3 h-3 mr-1" />
                            {manuscript?.hasSample ? 'Edit Sample' : 'Create a Sample'}
                          </Button>
                          {/* Preview Sample Button - Only visible when sample exists */}
                          {manuscript?.hasSample && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handlePreviewSample}
                              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 px-2"
                              data-testid="preview-sample-btn"
                              title="Preview your sample"
                            >
                              <Play className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        
                        {/* Share Preview URL Section */}
                        {manuscript?.hasSample && previewShareUrl && (
                          <div className="p-3 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <LinkIcon className="w-3 h-3 text-purple-400" />
                              <span className="text-xs font-medium text-purple-300">Share Sample Link</span>
                            </div>
                            
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  readOnly
                                  value={previewShareUrl}
                                  className="flex-1 bg-slate-800/50 border border-slate-600/50 rounded px-2 py-1.5 text-xs text-slate-300 truncate"
                                  data-testid="preview-share-url"
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={copyPreviewUrl}
                                  className={`px-2 ${copySuccess ? 'text-green-400' : 'text-purple-400 hover:text-purple-300'}`}
                                  data-testid="copy-preview-url-btn"
                                  title="Copy to clipboard"
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                                <a
                                  href={previewShareUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-purple-400 hover:text-purple-300 p-1"
                                  title="Open in new tab"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              </div>
                              <p className="text-[9px] text-slate-500 italic">
                                Share this link on social media to let potential readers preview your sample.
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {/* Post-Ingestion Outline Editor - For editing after TTS completion */}
                        <Button
                          size="sm"
                          onClick={() => setShowPostEditModal(true)}
                          className="w-full bg-yellow-400 text-black hover:bg-yellow-500 hover:text-black"
                          data-testid="post-edit-outline-btn"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Outline Editor
                        </Button>
                        <p className="text-[10px] text-slate-500 text-center -mt-2 mb-2">
                          Edit text & reorder after ingestion
                        </p>
                        
                        {/* Activate/Deactivate */}
                        {manuscript?.is_active ? (
                          <Button
                            className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white"
                            disabled={isActivating}
                            onClick={() => {
                              setActivationAction('deactivate');
                              setShowActivationModal(true);
                            }}
                            data-testid="deactivate-btn"
                          >
                            🔴 Deactivate
                          </Button>
                        ) : (
                          <Button
                            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white"
                            disabled={isActivating}
                            onClick={() => {
                              setActivationAction('activate');
                              setShowActivationModal(true);
                            }}
                            data-testid="activate-btn"
                          >
                            🟢 Activate
                          </Button>
                        )}

                        {/* SEO Index — Phase 3 */}
                        {manuscript?.is_active === true && (
                          manuscript?.seo_indexed ? (
                            <div className="w-full text-center space-y-1 pt-1" data-testid="seo-indexed-label">
                              <p className="text-xs font-semibold text-emerald-400">&#10003; Manuscript Indexed</p>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(`https://www.newaudia.com/book/${manuscript.seo_slug}`);
                                  toast.success('Public URL copied');
                                }}
                                className="text-[10px] text-blue-400 hover:text-blue-300 underline break-all"
                                data-testid="seo-copy-url"
                              >
                                newaudia.com/book/{manuscript.seo_slug}
                              </button>
                            </div>
                          ) : (
                            <div className="w-full space-y-1" data-testid="seo-index-container">
                              <Button
                                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white"
                                disabled={indexingInFlight}
                                onClick={handleIndexManuscript}
                                data-testid="seo-index-btn"
                              >
                                {indexingInFlight ? 'Indexing…' : 'Index'}
                              </Button>
                              {indexError && (
                                <p className="text-[10px] text-red-400 text-center">{indexError}</p>
                              )}
                            </div>
                          )
                        )}
                      </div>
                    );
                  }
                  
                  // PHASE 5: STATE: PARTIAL AUDIO - Some segments completed, but not all
                  // This happens when user closes Gate 6 early
                  if (manuscript?.audio_status === 'partial' || processingStatus?.status === 'partial') {
                    const completedCount = manuscript?.audio_segments_completed || processingStatus?.completedCount || 0;
                    const totalCount = manuscript?.audio_segments_total || processingStatus?.totalCount || 0;
                    const remainingCount = totalCount - completedCount;
                    
                    return (
                      <div className="space-y-4">
                        {/* Partial Audio Warning */}
                        <div className="text-center p-4 bg-amber-900/30 border border-amber-500/50 rounded-xl">
                          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-2" />
                          <p className="text-lg text-amber-300 font-semibold">Audio Incomplete</p>
                          <p className="text-sm text-slate-300 mt-2">
                            <strong>{completedCount}</strong> of <strong>{totalCount}</strong> segments have audio.
                          </p>
                          <p className="text-xs text-slate-400 mt-2">
                            Complete the remaining {remainingCount} segment{remainingCount !== 1 ? 's' : ''} before activating.
                          </p>
                        </div>
                        
                        {/* Finish Audio Processing Button */}
                        <Button
                          className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white"
                          onClick={() => {
                            console.log('[EditTitlePage] Opening Gate 6A resume modal');
                            setShowResumeModal(true);
                          }}
                          data-testid="finish-audio-btn"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Finish Audio Processing ({remainingCount} remaining)
                        </Button>
                        
                        <div className="grid grid-cols-2 gap-2">
                          {/* Preview - DISABLED for partial as per user request */}
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={true}
                            className="text-xs opacity-50 cursor-not-allowed"
                            title="Preview available after audio is complete"
                            data-testid="preview-btn-disabled"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Preview
                          </Button>
                        </div>
                        
                        {/* Activate Button - DISABLED for partial */}
                        <Button
                          className="w-full bg-slate-700/50 text-slate-400 cursor-not-allowed"
                          disabled={true}
                          title="Complete audio processing before activating"
                          data-testid="activate-btn-disabled"
                        >
                          🟢 Activate (Audio Incomplete)
                        </Button>
                        
                        <p className="text-xs text-slate-500 text-center">
                          💡 Your progress has been saved. Finish processing anytime.
                        </p>
                      </div>
                    );
                  }
                  
                  // STATE: DONE but can_activate=false = Finalizing
                  if (ttsState === 'DONE' && !canActivate) {
                    return (
                      <div className="space-y-4">
                        <div className="text-center p-4 bg-amber-900/20 border border-amber-500/30 rounded-xl">
                          <Loader2 className="w-10 h-10 text-amber-400 mx-auto mb-2 animate-spin" />
                          <p className="text-lg text-amber-300 font-semibold">Finalizing</p>
                          <p className="text-xs text-slate-400 mt-1">
                            TTS complete, waiting for finalization
                          </p>
                          <p className="text-xs text-slate-500 mt-1 font-mono">
                            {activationReason}
                          </p>
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowOutlineModal(true)}
                          className="w-full"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          View Outline
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowProofReport(true)}
                          className="w-full text-indigo-400 border-indigo-500/30 hover:bg-indigo-900/20"
                          data-testid="proof-report-btn"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Generate TTS Proof Report
                        </Button>
                      </div>
                    );
                  }
                  
                  // STATE: BLOCKED
                  if (ttsState === 'BLOCKED') {
                    return (
                      <div className="space-y-4">
                        <div className="text-center p-4 bg-red-900/20 border border-red-500/30 rounded-xl">
                          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-2" />
                          <p className="text-lg text-red-300 font-semibold">Blocked</p>
                          <p className="text-xs text-slate-400 mt-2">
                            {blockingReason || 'Processing blocked'}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            Support has been notified.
                          </p>
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowProofReport(true)}
                          className="w-full text-indigo-400 border-indigo-500/30 hover:bg-indigo-900/20"
                          data-testid="proof-report-btn"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Generate TTS Proof Report
                        </Button>
                      </div>
                    );
                  }
                  
                  // STATE: FAIL
                  if (ttsState === 'FAIL') {
                    return (
                      <div className="space-y-4">
                        <div className="text-center p-4 bg-red-900/20 border border-red-500/30 rounded-xl">
                          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-2" />
                          <p className="text-lg text-red-300 font-semibold">Failed</p>
                          <p className="text-xs text-slate-400 mt-2">
                            {failReason || 'TTS generation failed'}
                          </p>
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowProofReport(true)}
                          className="w-full text-indigo-400 border-indigo-500/30 hover:bg-indigo-900/20"
                          data-testid="proof-report-btn"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Generate TTS Proof Report
                        </Button>
                      </div>
                    );
                  }
                  
                  // STATE: EXECUTING or FINALIZING - show progress
                  if (ttsState === 'EXECUTING' || ttsState === 'FINALIZING') {
                    return (
                      <div className="space-y-4">
                        <div className="text-center p-4 bg-slate-800/50 border border-slate-700 rounded-xl">
                          <Loader2 className="w-10 h-10 text-indigo-400 mx-auto mb-2 animate-spin" />
                          <p className="text-lg text-slate-300 font-semibold">
                            {ttsState === 'EXECUTING' ? 'Generating Audio' : 'Finalizing'}
                          </p>
                          
                          {/* Progress bar - MANDATORY per spec */}
                          {totalSegments > 0 && (
                            <div className="mt-3 space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-500">Progress</span>
                                <span className="text-slate-300 font-mono" data-testid="tts-progress-count">
                                  {completedSegments}/{totalSegments}
                                </span>
                              </div>
                              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-indigo-500 transition-all duration-500"
                                  style={{ width: `${percent}%` }}
                                  data-testid="tts-progress-bar"
                                />
                              </div>
                              <p className="text-xs text-slate-500 text-center">
                                {percent}% complete
                              </p>
                            </div>
                          )}
                          
                          <p className="text-xs text-slate-500 mt-2">
                            You can safely close this page. Processing continues in the background.
                          </p>
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowOutlineModal(true)}
                          className="w-full"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          View Outline
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowProofReport(true)}
                          className="w-full text-indigo-400 border-indigo-500/30 hover:bg-indigo-900/20"
                          data-testid="proof-report-btn"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Generate TTS Proof Report
                        </Button>
                      </div>
                    );
                  }
                  
                  // STATE: QUEUED - waiting to start
                  if (ttsState === 'QUEUED') {
                    return (
                      <div className="space-y-4">
                        <div className="text-center p-4 bg-slate-800/50 border border-slate-700 rounded-xl">
                          <Clock className="w-10 h-10 text-amber-400 mx-auto mb-2" />
                          <p className="text-lg text-slate-300 font-semibold">Queued</p>
                          <p className="text-xs text-slate-500 mt-2">
                            Waiting to start audio generation
                          </p>
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowOutlineModal(true)}
                          className="w-full"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          View Outline
                        </Button>
                      </div>
                    );
                  }
                  
                  // STATE: NONE - no TTS job (default fallback)
                  return (
                    <div className="space-y-4">
                      <div className="text-center p-4 bg-slate-800/50 border border-slate-700 rounded-xl">
                        <Volume2 className="w-10 h-10 text-slate-500 mx-auto mb-2" />
                        <p className="text-lg text-slate-400 font-semibold">No Audio</p>
                        <p className="text-xs text-slate-500 mt-1">
                          Audio generation has not started
                        </p>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowOutlineModal(true)}
                        className="w-full"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        View Outline
                      </Button>
                    </div>
                  );
                })()}
              </section>
            )}

            {/* Book Stats */}
            {manuscript && (
              <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-5">
                <h2 className="text-lg font-semibold mb-4">Book Stats</h2>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Words</span>
                    <span className="text-slate-200">{(manuscript.word_count || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Chapters</span>
                    <span className="text-slate-200">{manuscript.chapter_count || manuscript.segment_count || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Segments</span>
                    <span className="text-slate-200">{manuscript.segment_count || 0}</span>
                  </div>
                  {/* <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Processing Cost</span>
                    <span className="text-emerald-400">${(manuscript.api_cost_total || 0).toFixed(4)}</span>
                  </div> */}
                  {/* {(manuscript.tts_editing_cost_total > 0) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">TTS Editing Costs</span>
                      <span className="text-amber-400">${(manuscript.tts_editing_cost_total || 0).toFixed(4)}</span>
                    </div>
                  )} */}
                  {/* EI Status */}
                  {title?.ei_status && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400 flex items-center gap-1">
                        <Brain className="w-3 h-3" />
                        EI Status
                      </span>
                      <span className={`${
                        title.ei_status === 'completed' ? 'text-purple-400' :
                        title.ei_status === 'running' ? 'text-blue-400' :
                        title.ei_status === 'failed' ? 'text-red-400' :
                        'text-slate-400'
                      }`}>
                        {title.ei_status === 'completed' ? 'Ready' :
                         title.ei_status === 'running' ? `${title.ei_completed || 0}/${title.ei_total || 0}` :
                         title.ei_status === 'failed' ? 'Failed' :
                         title.ei_status === 'queued' ? 'Queued' : title.ei_status}
                      </span>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>

          {/* Right Column - Metadata */}
          <div className="lg:col-span-2">
            <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6">
              <h2 className="text-lg font-semibold mb-4">Title Information</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="title_name">Title Name *</Label>
                  <Input
                    id="title_name"
                    name="title_name"
                    value={formData.title_name}
                    onChange={handleChange}
                    className="mt-1 bg-slate-800 border-slate-600"
                  />
                </div>
                
                <div>
                  <Label htmlFor="subtitle">Subtitle</Label>
                  <Input
                    id="subtitle"
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleChange}
                    className="mt-1 bg-slate-800 border-slate-600"
                  />
                </div>
                
                <div>
                  <Label htmlFor="isbn">ISBN</Label>
                  <Input
                    id="isbn"
                    name="isbn"
                    value={formData.isbn}
                    onChange={handleChange}
                    className="mt-1 bg-slate-800 border-slate-600"
                  />
                </div>
                
                <div>
                  <Label>Book Type</Label>
                  <div className="mt-1 flex rounded-lg overflow-hidden border border-slate-600">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, book_type: 'fiction', genre: '', theme: '' }))}
                      className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors ${
                        formData.book_type === 'fiction'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      Fiction
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, book_type: 'non-fiction', genre: '', theme: '' }))}
                      className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors border-l border-slate-600 ${
                        formData.book_type === 'non-fiction'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      Non-Fiction
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="genre">Genre</Label>
                    <select
                      id="genre"
                      name="genre"
                      value={formData.genre}
                      onChange={(e) => {
                        // Reset theme when genre changes
                        setFormData(prev => ({ 
                          ...prev, 
                          genre: e.target.value,
                          theme: '' 
                        }));
                      }}
                      className="mt-1 w-full rounded-md bg-slate-800 border border-slate-600 px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    >
                      {getGenresForBookType(formData.book_type).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="theme">Theme</Label>
                    <select
                      id="theme"
                      name="theme"
                      value={formData.theme}
                      onChange={handleChange}
                      className="mt-1 w-full rounded-md bg-slate-800 border border-slate-600 px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    >
                      {getThemesForGenre(formData.genre).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="purchase_url">Purchase URL</Label>
                  <Input
                    id="purchase_url"
                    name="purchase_url"
                    value={formData.purchase_url}
                    onChange={handleChange}
                    placeholder="https://example.com/book"
                    className="mt-1 bg-slate-800 border-slate-600"
                  />
                </div>
                
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="description">Description (About the Book)</Label>
                    {descriptionChanged && (
                      <span className="text-xs text-amber-400 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Unsaved changes
                      </span>
                    )}
                  </div>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={8}
                    placeholder="This description will appear in the 'About the Book' section. It is automatically generated when you activate the book, but you can edit it here."
                    className={`mt-1 bg-slate-800 border-slate-600 resize-none ${descriptionChanged ? 'border-amber-500 ring-1 ring-amber-500/50' : ''}`}
                  />
                </div>
                
                <div className="pt-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_promotional"
                      name="is_promotional"
                      checked={formData.is_promotional}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-600"
                    />
                    <Label htmlFor="is_promotional" className="cursor-pointer text-sm whitespace-nowrap">
                      Promotional (Free)
                    </Label>
                  </div>
                  
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-2 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white font-semibold transition-colors disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </section>

            {/* Book History Card - Full width in right column */}
            {manuscript && (
              <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6 mt-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-indigo-400" />
                  Book History
                </h2>
                {loadingHistory ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : bookHistory ? (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                    <div className="text-center p-3 bg-slate-800/50 rounded-xl">
                      <p className="text-xs text-slate-400 mb-1">Upload Date</p>
                      <p className="text-sm font-semibold text-slate-200">
                        {bookHistory.upload_date 
                          ? new Date(bookHistory.upload_date).toLocaleDateString()
                          : 'N/A'}
                      </p>
                    </div>
                    <div className="text-center p-3 bg-slate-800/50 rounded-xl">
                      <p className="text-xs text-slate-400 mb-1">Last Accessed</p>
                      <p className="text-sm font-semibold text-slate-200">
                        {bookHistory.date_last_accessed 
                          ? new Date(bookHistory.date_last_accessed).toLocaleDateString()
                          : 'Never'}
                      </p>
                    </div>
                    <div className="text-center p-3 bg-slate-800/50 rounded-xl">
                      <p className="text-xs text-slate-400 mb-1">Days Available</p>
                      <p className="text-lg font-semibold text-slate-200">{bookHistory.days_available || 0}</p>
                    </div>
                    <div className="text-center p-3 bg-slate-800/50 rounded-xl">
                      <p className="text-xs text-slate-400 mb-1 flex items-center justify-center gap-1">
                        <Users className="w-3 h-3" /> Unique Readers
                      </p>
                      <p className="text-lg font-semibold text-indigo-400">{bookHistory.unique_readers || 0}</p>
                    </div>
                    <div className="text-center p-3 bg-slate-800/50 rounded-xl">
                      <p className="text-xs text-slate-400 mb-1">Guest Readers</p>
                      <p className="text-lg font-semibold text-purple-400">{bookHistory.guest_readers || 0}</p>
                    </div>
                    <div className="text-center p-3 bg-slate-800/50 rounded-xl">
                      <p className="text-xs text-slate-400 mb-1">Avg. Readers/Day</p>
                      <p className="text-lg font-semibold text-slate-200">{bookHistory.avg_readers_per_day || 0}</p>
                    </div>
                    <div className="text-center p-3 bg-slate-800/50 rounded-xl">
                      <p className="text-xs text-slate-400 mb-1">Total Words Read</p>
                      <p className="text-lg font-semibold text-emerald-400">{(bookHistory.total_words_read || 0).toLocaleString()}</p>
                    </div>
                    <div className="text-center p-3 bg-slate-800/50 rounded-xl">
                      <p className="text-xs text-slate-400 mb-1">Engagement (mins)</p>
                      <p className="text-lg font-semibold text-cyan-400">{bookHistory.total_engagement_minutes || 0}</p>
                    </div>
                    <div className="text-center p-3 bg-slate-800/50 rounded-xl">
                      <p className="text-xs text-slate-400 mb-1 flex items-center justify-center gap-1">
                        <MessageSquare className="w-3 h-3" /> Audia Questions
                      </p>
                      <p className="text-lg font-semibold text-amber-400">{bookHistory.total_audia_questions || 0}</p>
                    </div>
                    <div className="text-center p-3 bg-gradient-to-br from-yellow-900/30 to-orange-900/30 rounded-xl border border-yellow-700/30">
                      <p className="text-xs text-yellow-300/80 mb-1 flex items-center justify-center gap-1">
                        💰 Reader Credits
                      </p>
                      <p className="text-lg font-bold text-yellow-400">{(bookHistory.total_reader_credits || 0).toFixed(2)}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-2">No history data available</p>
                )}
              </section>
            )}
          </div>
        </div>
      </main>

      {/* P0 FINAL HOLE: TTSDecisionModal REMOVED - TTS is system-owned */}
      {/* No user action for TTS generation is allowed */}

      {/* P0 DIRECTIVE: TTSProgressModal is FORBIDDEN and has been REMOVED */}
      {/* TTS is auto-triggered and monitored by backend only */}

      {/* Outline Editor Modal - V3 with text editing */}
      {/* P0 HARD START: Outline save does NOT auto-trigger TTS */}
      {/* User must explicitly start TTS from the Audio Status section */}
      <OutlineViewerModalV3
        isOpen={showOutlineModal}
        onClose={() => setShowOutlineModal(false)}
        manuscriptId={manuscript?.id}
        manuscriptTitle={title?.title_name}
        isStandalone={true}
        onSaveAndContinue={async (result) => {
          console.log('[EditTitlePage] Outline saved:', result);
          setShowOutlineModal(false);
          
          // Store the updated character count for accurate TTS cost estimation
          if (result?.totalCharacterCount != null) {
            console.log('[EditTitlePage] Updating character count for TTS estimation:', result.totalCharacterCount);
            setUpdatedCharacterCount(result.totalCharacterCount);
          }
          
          // P0 HARD START: Do NOT auto-trigger TTS after outline save
          // User must explicitly start TTS via the Audio Status section
          // Just refresh the title data
          fetchTitle();
          
          // If segments were modified, show a notification
          if (result?.needsTTS && result.modifiedSegments?.length > 0) {
            console.log('[EditTitlePage] TTS needed for segments (user must start manually):', result.modifiedSegments);
            // Optionally show a notification that TTS is available
          }
        }}
      />

      {/* Post-Ingestion Outline Editor Modal - For editing after TTS completion */}
      <PostIngestionOutlineEditor
        isOpen={showPostEditModal}
        onClose={() => setShowPostEditModal(false)}
        manuscriptId={title?.manuscript_id}
        onSaveComplete={handlePostEditComplete}
      />

      {/* Activation Modal */}
      {/* Cover Image Options Modal */}
      <Dialog open={showCoverModal} onOpenChange={setShowCoverModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-50 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Cover Image</DialogTitle>
            <DialogDescription className="text-slate-400">
              Choose an action for the book cover
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <Button
              onClick={() => coverInputRef.current?.click()}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="cover-upload-new-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload New Cover
            </Button>
            {coverUrl && (
              <Button
                onClick={handleOptimizeExistingCover}
                variant="outline"
                className="w-full border-teal-600/50 text-teal-400 hover:bg-teal-600/10"
                data-testid="cover-optimize-btn"
              >
                <RefreshCcw className="w-4 h-4 mr-2" />
                Optimize Existing Cover
              </Button>
            )}
            <Button
              onClick={() => setShowCoverModal(false)}
              variant="ghost"
              className="w-full text-slate-400 hover:text-white"
              data-testid="cover-cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showActivationModal} onOpenChange={setShowActivationModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {activationAction === 'activate' ? (
                <>
                  <div className="w-8 h-8 rounded-full bg-green-600/20 flex items-center justify-center">
                    🟢
                  </div>
                  Activate Book
                </>
              ) : (
                <>
                  <div className="w-8 h-8 rounded-full bg-red-600/20 flex items-center justify-center">
                    🔴
                  </div>
                  Deactivate Book
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {activationAction === 'activate' 
                ? 'Choose what processes to run when activating this book:'
                : 'Are you sure you want to deactivate this book? It will no longer be available to readers.'
              }
            </DialogDescription>
          </DialogHeader>

          {activationAction === 'activate' && (
            <div className="py-4 space-y-3">
              <p className="text-sm text-slate-300">
                Activating this book will:
              </p>
              <ul className="text-sm text-slate-400 space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">✓</span>
                  <span>Generate an AI-powered book description</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">✓</span>
                  <span>Create a Book Semantic Profile (BSP) for recommendations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">✓</span>
                  <span>Make the book available to readers in the library</span>
                </li>
              </ul>
              <p className="text-xs text-slate-500 mt-3 border-t border-slate-700 pt-3">
                This process may take a few moments to complete.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowActivationModal(false)}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (activationAction === 'deactivate') {
                  setShowActivationModal(false);
                  // Simple deactivation - no progress needed
                  setIsActivating(true);
                  try {
                    const token = localStorage.getItem('accessToken');
                    await fetch(`${backendUrl}/api/manuscripts/${manuscript.id}/toggle-activation`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    await fetchTitle();
                  } catch (error) {
                    notifyError('Failed to deactivate book');
                  } finally {
                    setIsActivating(false);
                  }
                } else {
                  // Phase 3: Run payment gate before activation
                  await handleActivationWithPaymentGate(async () => {
                    setShowActivationModal(false);
                    // Activation - show progress modal
                  setActivationProgressSteps([
                    { id: 'summary', label: 'Generating AI description...', status: 'pending' },
                    { id: 'bsp', label: 'Creating book semantic profile...', status: 'pending' },
                    { id: 'activate', label: 'Activating book...', status: 'pending' },
                    { id: 'insights', label: 'Generating AI insights...', status: 'pending' }
                  ]);
                  setShowActivationProgress(true);
                  setActivationProgressStep('Starting activation...');
                  
                  try {
                    const token = localStorage.getItem('accessToken');
                    
                    // Step 1: Summary
                    setActivationProgressSteps(prev => prev.map(s => 
                      s.id === 'summary' ? { ...s, status: 'active' } : s
                    ));
                    setActivationProgressStep('Generating AI-powered book description...');
                    
                    // Make the API call - uses reactivate-with-reprocess which generates description + BSP
                    const response = await fetch(`${backendUrl}/api/manuscripts/${manuscript.id}/reactivate-with-reprocess`, {
                      method: 'POST',
                      headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({
                        regenerate_summary: true,
                        reprocess_bsp: true,
                        reprocess_tts: false
                      })
                    });
                    
                    if (response.ok) {
                      const data = await response.json();
                      
                      // Update description field if summary was generated
                      if (data.summary) {
                        setFormData(prev => ({ ...prev, description: data.summary }));
                        setOriginalDescription(data.summary);
                      }
                      
                      // Step 2: BSP complete
                      setActivationProgressSteps(prev => prev.map(s => 
                        s.id === 'summary' ? { ...s, status: 'complete' } :
                        s.id === 'bsp' ? { ...s, status: 'complete' } : s
                      ));
                      
                      // Step 3: Activate complete
                      setActivationProgressSteps(prev => prev.map(s => 
                        s.id === 'activate' ? { ...s, status: 'complete' } : s
                      ));
                      setActivationProgressStep('Finalizing activation...');
                      
                      await new Promise(resolve => setTimeout(resolve, 300));
                      
                      // Step 4: EI/Insights - check what was triggered
                      const processes = data.processes_triggered || [];
                      const ei_status = processes.includes('bsp') ? 'queued' : 'skipped';
                      const ei_message = processes.includes('bsp') ? 'BSP generated' : 'BSP skipped';
                      
                      setActivationProgressSteps(prev => prev.map(s => 
                        s.id === 'insights' ? { ...s, status: 'active' } : s
                      ));
                      
                      if (ei_status === 'queued') {
                        setActivationProgressStep('Generating AI insights (runs in background)...');
                        await new Promise(resolve => setTimeout(resolve, 500));
                        setActivationProgressSteps(prev => prev.map(s => 
                          s.id === 'insights' ? { ...s, status: 'complete' } : s
                        ));
                      } else if (ei_status === 'skipped') {
                        setActivationProgressStep(ei_message || 'AI insights skipped');
                        await new Promise(resolve => setTimeout(resolve, 400));
                        setActivationProgressSteps(prev => prev.map(s => 
                          s.id === 'insights' ? { ...s, status: 'complete', label: 'AI insights (skipped)' } : s
                        ));
                      } else {
                        setActivationProgressSteps(prev => prev.map(s => 
                          s.id === 'insights' ? { ...s, status: 'complete' } : s
                        ));
                      }
                      
                      setActivationProgressStep('Book activated successfully!');
                      
                      // Auto-index on first activation
                      if (!manuscript.seo_indexed) {
                        try {
                          const indexRes = await xhrFetchAuthPost(`${backendUrl}/api/manuscripts/${manuscript.id}/index`, {});
                          if (indexRes.ok && indexRes.json?.seo_slug) {
                            setManuscript(prev => ({ ...prev, seo_indexed: true, seo_slug: indexRes.json.seo_slug }));
                          }
                        } catch {
                          toast.warning('Activated — indexing failed, use the Index button to retry');
                        }
                      }

                      await new Promise(resolve => setTimeout(resolve, 800));
                      setShowActivationProgress(false);
                      
                      await fetchTitle();
                    } else {
                      const errorData = await response.json().catch(() => ({}));
                      setShowActivationProgress(false);
                      notifyError(errorData.detail || 'Failed to activate book');
                    }
                  } catch (error) {
                    console.error('Activation error:', error);
                    setShowActivationProgress(false);
                    notifyError('Failed to activate book');
                  }
                  });
                }
              }}
              disabled={isActivating}
              className={activationAction === 'activate' 
                ? "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500" 
                : "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600"
              }
            >
              {isActivating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {activationAction === 'activate' ? 'Activating...' : 'Deactivating...'}
                </>
              ) : (
                activationAction === 'activate' ? '🟢 Activate' : '🔴 Deactivate'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activation Progress Modal */}
      <Dialog open={showActivationProgress} onOpenChange={() => {}}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-50 max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-green-500" />
              Activating Book
            </DialogTitle>
            <DialogDescription asChild>
              <div className="pt-4 space-y-4">
                <p className="text-slate-400">
                  Preparing &ldquo;{manuscript?.title_name || manuscript?.title}&rdquo; for the public library...
                </p>
                
                {/* Progress Steps */}
                <div className="space-y-3">
                  {activationProgressSteps.map((step) => (
                    <div key={step.id} className="flex items-center gap-3">
                      {step.status === 'pending' && (
                        <div className="w-5 h-5 rounded-full border-2 border-slate-600" />
                      )}
                      {step.status === 'active' && (
                        <Loader2 className="w-5 h-5 animate-spin text-green-500" />
                      )}
                      {step.status === 'complete' && (
                        <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                      <span className={`text-sm ${
                        step.status === 'complete' ? 'text-green-400 font-medium' :
                        step.status === 'active' ? 'text-slate-200 font-medium' :
                        'text-slate-500'
                      }`}>
                        {step.label}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Current Step Detail */}
                {activationProgressStep && (
                  <div className="bg-slate-800/50 rounded-lg p-3 mt-4">
                    <p className="text-sm text-slate-400">{activationProgressStep}</p>
                  </div>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
      
      {/* TTS Regeneration Progress Modal */}
      <Dialog open={showTTSRegenModal} onOpenChange={(open) => {
        // Only allow closing if not processing
        if (!ttsRegenStatus.isProcessing) {
          setShowTTSRegenModal(open);
        }
      }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {ttsRegenStatus.isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
                  Regenerating Audio
                </>
              ) : ttsRegenStatus.error ? (
                <>
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  Regeneration Failed
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  Regeneration Complete
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {ttsRegenStatus.isProcessing 
                ? 'Processing modified segments. This may take a moment...'
                : ttsRegenStatus.error 
                  ? ttsRegenStatus.error
                  : 'All modified segments have been regenerated successfully.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Progress</span>
                <span className="text-white font-medium">
                  {ttsRegenStatus.segmentsCompleted} / {ttsRegenStatus.segmentsToProcess.length} segments
                </span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all duration-500"
                  style={{ 
                    width: `${(ttsRegenStatus.segmentsCompleted / Math.max(ttsRegenStatus.segmentsToProcess.length, 1)) * 100}%` 
                  }}
                />
              </div>
            </div>
            
            {/* Current Segment */}
            {ttsRegenStatus.isProcessing && ttsRegenStatus.currentSegment && (
              <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                <div className="text-xs text-slate-500 mb-1">Currently processing:</div>
                <div className="text-sm text-white font-medium">
                  {ttsRegenStatus.currentSegment}
                </div>
              </div>
            )}
            
            {/* Segment List */}
            <div className="max-h-40 overflow-y-auto space-y-1">
              {ttsRegenStatus.segmentsToProcess.map((segId, idx) => (
                <div 
                  key={segId}
                  className={`flex items-center gap-2 text-sm px-2 py-1 rounded ${
                    idx < ttsRegenStatus.segmentsCompleted 
                      ? 'text-green-400' 
                      : idx === ttsRegenStatus.segmentsCompleted && ttsRegenStatus.isProcessing
                        ? 'text-purple-400'
                        : 'text-slate-500'
                  }`}
                >
                  {idx < ttsRegenStatus.segmentsCompleted ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : idx === ttsRegenStatus.segmentsCompleted && ttsRegenStatus.isProcessing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Clock className="w-4 h-4" />
                  )}
                  <span>{segId}</span>
                </div>
              ))}
            </div>
          </div>
          
          {!ttsRegenStatus.isProcessing && (
            <DialogFooter>
              <Button
                onClick={() => setShowTTSRegenModal(false)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {ttsRegenStatus.error ? 'Close' : 'Done'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
      
      {/* P0 PROOF REPORT: One-click diagnostic package */}
      <TTSProofReportModal
        open={showProofReport}
        onOpenChange={setShowProofReport}
        titleId={titleId}
        titleName={title?.title_name || title?.title}
      />
      
      {/* PHASE 6: Gate 6A Resume Modal for Partial Audio Completion */}
      <StreamlinedTTSResumeModal
        isOpen={showResumeModal}
        jobId={manuscript?.simple_ingest_job_id}
        authorId={title?.publisher_author_id}
        remainingCount={(manuscript?.audio_segments_total || 0) - (manuscript?.audio_segments_completed || 0)}
        totalCount={manuscript?.audio_segments_total || 0}
        onComplete={() => {
          console.log('[EditTitlePage] Audio resume complete - refreshing page');
          setShowResumeModal(false);
          // Reload page to show updated status
          window.location.reload();
        }}
        onClose={() => setShowResumeModal(false)}
      />
      
      {/* Book Sample Selection Modal */}
      <SampleSelectionModal
        isOpen={showSampleModal}
        onClose={() => setShowSampleModal(false)}
        manuscriptId={manuscript?.id}
        hasExistingSample={manuscript?.hasSample || false}
        onSampleCreated={() => fetchTitle()}
      />
      
      <UniversalFooter />

      {/* Phase 3: Payment gate modals */}
      <ActivationChargeConfirmModal
        isOpen={showChargeConfirmModal}
        onConfirm={handleChargeConfirmedAndActivate}
        onCancel={() => setShowChargeConfirmModal(false)}
        last4={chargeCardInfo.last4}
        brand={chargeCardInfo.brand}
      />
      <PaymentMethodRequiredModal
        isOpen={showPaymentMethodModal}
        onSuccess={handlePaymentMethodAddedForActivation}
        onDismiss={() => setShowPaymentMethodModal(false)}
      />
    </div>
  );
};

export default EditTitlePage;
